import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  /**
   * The last line clamp we applied. Used to bail out before writing the same value again.
   * @type {number | null}
   */
  #lastMaxLines = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.#handleResize);

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      window.addEventListener('resize', this.#handleResize);
      this.#calculateTruncation();
    }
  }

  /**
   * Calculate truncation for the title.
   *
   * The observer watches `this`, so this method must never write a layout-affecting style to `this`
   * itself: clamping would change its height, re-firing the observer, which would compute a smaller
   * clamp, and so on — a per-frame layout loop, one per product card on the page. Writing only to a
   * distinct inner text element keeps the observed box's height driven by CSS, not by this code.
   */
  #calculateTruncation() {
    const textElement = /** @type {HTMLElement | null} */ (this.refs.text ?? this.querySelector('.title-text'));

    // No inner element to clamp. Bail rather than falling back to `this` and creating the loop.
    if (!textElement || !textElement.textContent) return;

    const containerHeight = this.clientHeight;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    // `line-height: normal` parses to NaN, which used to produce `-webkit-line-clamp: NaN`.
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    if (!Number.isFinite(availableHeight) || availableHeight <= 0) return;

    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    // Nothing changed — skip the write so a fractional line-height cannot make us flap between
    // two values on every frame.
    if (maxLines === this.#lastMaxLines) return;
    this.#lastMaxLines = maxLines;

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Handle resize events. Held as a field so `removeEventListener` can actually match it — the
   * previous `.bind(this)` at registration produced a different function object every time.
   */
  #handleResize = () => {
    this.#calculateTruncation();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
